<script lang="ts">
import {
  AUTH_API_INJECTION_TOKEN,
  ACCOUNTS_API_INJECTION_TOKEN,
  USERS_API_INJECTION_TOKEN,
  APP_CONFIG,
  ExpirationOverlay,
} from '@onereach/auth-ui-module';
import { storeToRefs } from 'pinia';
import { defineComponent, provide } from 'vue';
import { RouterView } from 'vue-router';

import { TranslatableError } from '@/errors';
import SentryApi from '@/sentry';

import AccountsApi from './api/AccountsApi';
import AuthApi from './api/Auth';
import UsersApi from './api/UsersApi';
import { AppPage } from './components';
import config from './config';
import { useAuth } from './stores';

export default defineComponent({
  components: {
    AppPage,
    ExpirationOverlay,
    RouterView,
  },
  setup() {

    const authStore = useAuth();
    const {
      authorized: isAuthorized,
      username,
      identityProvider,
      reactiveToken,
      cookiesAnotherUser,
      cookiesExpired,
      cookiesNoRights,
      userId,
      multiUserId,
      accountId,
      role,
    } = storeToRefs(authStore);
    const {
      updateCookieExpiration,
      getUserNameForLockScreen,
    } = authStore;

    //provide api services to AuthPopover
    provide(AUTH_API_INJECTION_TOKEN, AuthApi);
    provide(ACCOUNTS_API_INJECTION_TOKEN, AccountsApi);
    provide(USERS_API_INJECTION_TOKEN, UsersApi);
    provide(APP_CONFIG, config);

    return {
      isAuthorized,
      multiUserId,
      cookiesAnotherUser,
      username,
      accountId,
      authUrl: config.AUTH_UI_URL,
      identityProvider,
      token: reactiveToken,
      userId,
      cookiesExpired,
      cookiesNoRights,
      updateCookieExpiration,
      getUserNameForLockScreen,
      role,
    };
  },
  data() {
    return {
      config,
      lockScreenUsername: '',
      errorText: this.$t('common.error') as string,
      showError: false,
    };
  },
  watch: {
    async isAuthorized(value: boolean): Promise<void> {
      if (value) {
        this.lockScreenUsername = await this.getUserNameForLockScreen();
      }
    },
  },
  errorCaptured(e) {
    if (e instanceof TranslatableError) {

      this.errorText = this.$t(e.translationKey);
      SentryApi.captureException(e.innerError);
    } else {
      this.errorText = this.$t('errors.commonText');
      SentryApi.captureException(e);
    }

    this.showError = true;
    return false;
  },
  methods: {
    cookiesRenewHandler(): void {
      this.updateCookieExpiration();
    },
  },
});
</script>

<template>
  <div class="h-screen w-full">
    <ExpirationOverlay
      :lock-username="lockScreenUsername"
      :username="username"
      :auth-url="authUrl"
      :identity-provider="identityProvider"
      :token="token"
      :user-id="userId"
      :account-id="accountId"
      :cookies-expired="cookiesExpired"
      :cookies-another-user="cookiesAnotherUser"
      :cookies-no-rights="cookiesNoRights"
      :login-overlay-disabled="false"
      @on-update-cookie-expiration="cookiesRenewHandler"
    />
    <AppPage
      :token="token"
      :config="config"
      :account-id="accountId"
      :error-text="errorText"
      :show-error="showError"
      :user-role="role"
      @update:show-error="showError = $event"
    >
      <RouterView />
    </AppPage>
  </div>
</template>
